import React from 'react';
import {useAuth} from '../../context/AuthContext';
import {useLang} from '../../i18n';
import {useNavigate,useLocation} from 'react-router-dom';
import {
  LayoutDashboard,Users,Package,FileText,Receipt,UserCog,
  ShoppingCart,Warehouse,Truck,Settings,Building2,CreditCard,
  BarChart3,Globe,Database,Monitor,ClipboardList,ArrowLeftRight,Calculator,TrendingDown,
} from 'lucide-react';

const NAV_SECTIONS=[
  {lk:'nav.main',items:[
    {p:'/',             lk:'nav.dashboard',    i:LayoutDashboard, pm:'dashboard.view'},
    {p:'/pos',          lk:'nav.pos',          i:ShoppingCart,    pm:'pos.access'},
    {p:'/pos-sessions', lk:'nav.posSessions',  i:Monitor,         pm:'pos.sessions_view'},
  ]},
  {lk:'nav.sales',items:[
    {p:'/orders',    lk:'nav.orders',    i:FileText, pm:'orders.view'},
    {p:'/invoices',  lk:'nav.invoices',  i:Receipt,  pm:'invoices.view'},
    {p:'/customers', lk:'nav.customers', i:Users,    pm:'customers.view'},
  ]},
  {lk:'nav.inventory',items:[
    {p:'/products',        lk:'nav.products',       i:Package,        pm:'products.view'},
    {p:'/master-data',     lk:'nav.masterData',     i:Database,       pm:'categories.manage'},
    {p:'/inventory',       lk:'nav.stock',          i:Warehouse,      pm:'inventory.view'},
    {p:'/purchase-orders', lk:'nav.purchaseOrders', i:ClipboardList,  pm:'purchase_orders.view'},
    {p:'/stock-transfers', lk:'nav.stockTransfers', i:ArrowLeftRight, pm:'stock_transfers.view'},
    {p:'/vendors',         lk:'nav.vendors',        i:Truck,          pm:'vendors.view'},
  ]},
  {lk:'nav.finance',items:[
    {p:'/item-commissions',lk:'nav.commissions',i:Calculator,    pm:'item_commissions.view_own'},
    {p:'/payments',        lk:'nav.payments',   i:CreditCard,    pm:'payments.view'},
    {p:'/expenses',        lk:'nav.expenses',   i:TrendingDown,  pm:'expenses.view'},
    {p:'/currencies',      lk:'nav.currencies', i:Globe,         pm:'currency.manage'},
  ]},
  {lk:'nav.system',items:[
    {p:'/branches',lk:'nav.branches',i:Building2,pm:'branches.view'},
    {p:'/users',   lk:'nav.users',   i:UserCog,  pm:'users.view'},
    {p:'/reports', lk:'nav.reports', i:BarChart3, pm:'reports.sales'},
    {p:'/settings',lk:'nav.settings',i:Settings,  pm:'settings.view'},
  ]},
];

export default function Sidebar({collapsed=false,mobileOpen=false,isMobile=false,onToggle}){
  const{hasPermission}=useAuth();
  const{t}=useLang();
  const nav=useNavigate();
  const loc=useLocation();

  // Show text labels?
  // Desktop: show when NOT collapsed
  // Mobile:  show when mobile drawer is open
  const showLabels = isMobile ? mobileOpen : !collapsed;

  // Inline style for the sidebar element itself
  const sidebarStyle = isMobile ? {
    // Mobile: off-screen by default, slides in when open
    position:'fixed',
    top:0,left:0,bottom:0,
    zIndex:300,
    width:240,
    transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition:'transform .25s cubic-bezier(.4,0,.2,1)',
    boxShadow: mobileOpen ? '4px 0 40px rgba(0,0,0,.6)' : 'none',
    overflowY:'auto',
  } : {
    // Desktop: sticky, width transitions
    position:'sticky',
    top:0,
    height:'100vh',
    overflowY:'auto',
    width: collapsed ? 64 : 240,
    transition:'width .2s ease',
    flexShrink:0,
  };

  return(
    <aside
      style={{
        background:'var(--surface)',
        borderRight:'1px solid var(--border)',
        display:'flex',
        flexDirection:'column',
        ...sidebarStyle,
      }}
    >
      {/* Brand row */}
      <div style={{
        padding:'16px 14px',
        borderBottom:'1px solid var(--border)',
        display:'flex',
        alignItems:'center',
        gap:10,
        flexShrink:0,
      }}>
        {showLabels&&(
          <h1 style={{fontSize:17,fontWeight:800,whiteSpace:'nowrap',flex:1}}>
            ERP<span style={{color:'var(--primary)'}}>hub</span>
          </h1>
        )}
      </div>

      {/* Nav items */}
      <nav style={{flex:1,padding:'10px 6px',overflowY:'auto'}}>
        {NAV_SECTIONS.map(section=>{
          const visible=section.items.filter(item=>hasPermission(item.pm));
          if(!visible.length) return null;
          return(
            <div key={section.lk}>
              {showLabels&&(
                <div style={{
                  fontSize:10,fontWeight:700,color:'var(--td)',
                  letterSpacing:1,padding:'14px 12px 6px',
                  textTransform:'uppercase',
                }}>
                  {t(section.lk)}
                </div>
              )}
              {visible.map(item=>{
                const active=loc.pathname===item.p||(item.p!=='/'&&loc.pathname.startsWith(item.p));
                return(
                  <button
                    key={item.p}
                    onClick={()=>nav(item.p)}
                    title={t(item.lk)}
                    style={{
                      display:'flex',
                      alignItems:'center',
                      gap: showLabels ? 10 : 0,
                      justifyContent: showLabels ? 'flex-start' : 'center',
                      width:'100%',
                      padding: showLabels ? '9px 12px' : '10px 0',
                      borderRadius:8,
                      border:'none',
                      background: active ? 'var(--pm)' : 'transparent',
                      color: active ? 'var(--primary)' : 'var(--tm)',
                      cursor:'pointer',
                      fontSize:13,
                      fontWeight: active ? 600 : 500,
                      marginBottom:1,
                      transition:'all .12s',
                      textAlign:'left',
                    }}
                    onMouseEnter={e=>{if(!active){e.currentTarget.style.background='var(--s2)';e.currentTarget.style.color='var(--text)';}}}
                    onMouseLeave={e=>{if(!active){e.currentTarget.style.background='transparent';e.currentTarget.style.color='var(--tm)';}}}
                  >
                    <item.i size={17} style={{flexShrink:0}}/>
                    {showLabels&&<span style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{t(item.lk)}</span>}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div style={{padding:12,borderTop:'1px solid var(--border)',flexShrink:0}}/>
    </aside>
  );
}
